import { motion } from 'framer-motion';
import PropTypes from 'prop-types';
import React, { forwardRef } from 'react';
import styled, { css } from 'styled-components';

const StyledSection = styled(motion.section)`
  background: ${(props) =>
    props.$isGradient
      ? `linear-gradient(45deg, ${props.$bgColorStart}, ${props.$bgColorMid}, ${props.$bgColorEnd})`
      : props.$bgColor};

  /* Only apply numeric padding if not in custom mode */
  ${(props) =>
    !props.$isCustom &&
    css`
      padding-top: ${props.$paddingTop}rem;
      padding-bottom: ${props.$paddingBottom}rem;
    `}

  h1 {
    color: ${(props) => props.$h1Color};
    font-size: ${(props) => props.$h1Size} !important;
  }
  h2 {
    color: ${(props) => props.$h2Color} !important;
  }
  h3 {
    color: ${(props) => props.$h3Color} !important;
  }
`;

// Define a mapping for standard paddings.
const paddingMap = {
  lg: { top: 6, bottom: 6 },
  md: { top: 4, bottom: 4 },
  sm: { top: 2, bottom: 2 },
  none: { top: 0, bottom: 0 },
  default: { top: 6, bottom: 6 },
};

const Section = forwardRef(
  (
    {
      padding = 'lg',
      children,
      classes = '',
      idName,
      bgColor,
      bgColorStart,
      bgColorMid,
      bgColorEnd,
      isGradient,
      h1Color,
      h1Size,
      h2Color,
      h3Color,
    },
    ref
  ) => {
    const isCustom = padding === 'custom';
    // Only use numeric padding if not in "custom" mode.
    const { top, bottom } = isCustom
      ? { top: 0, bottom: 0 }
      : paddingMap[padding] || paddingMap.default;

    return (
      <StyledSection
        ref={ref}
        $bgColor={bgColor}
        $bgColorStart={bgColorStart}
        $bgColorMid={bgColorMid}
        $bgColorEnd={bgColorEnd}
        $isGradient={isGradient}
        $h1Size={h1Size}
        $h1Color={h1Color}
        $h2Color={h2Color}
        $h3Color={h3Color}
        $paddingTop={top}
        $paddingBottom={bottom}
        $isCustom={isCustom}
        className={`relative ${classes}`}
        id={idName}
      >
        {children}
      </StyledSection>
    );
  }
);

Section.propTypes = {
  children: PropTypes.node.isRequired,
};

Section.displayName = 'Section';

export { Section };
